import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

// The machine data shows the line has been standing for 30 minutes (setting
// stoppage_alert_minutes) and nobody declared cleaning / breakdown. Rendered by Layout for
// line operators, so it shows up on whatever screen they are on, and it stays until the
// operator gives the reason of the stoppage.

interface Stoppage {
  id: number;
  started_at: string;
  started_at_local: string;
  stopped_minutes: number;
}

export const STOPPAGE_REASONS = ['breakdown', 'cleaning', 'changeover', 'no_material', 'no_dye', 'other'] as const;
type StoppageReason = typeof STOPPAGE_REASONS[number];

const POLL_MS = 60_000;

const formatDuration = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
};

const LineStoppageAlertModal: React.FC = () => {
  const { user, selectedLine } = useAuth();
  const { t } = useLanguage();
  const [stoppage, setStoppage] = useState<Stoppage | null>(null);
  const [reason, setReason] = useState<StoppageReason | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const lineId = selectedLine?.id ?? null;
  const enabled = !!user && user.role === 'user' && !!lineId;

  const fetchAlert = useCallback(async () => {
    if (!enabled || !lineId) return;
    try {
      const res = await api.get('/line-stoppages/alert', { params: { line_id: lineId } });
      setStoppage(res.data.stoppage ?? null);
      setNow(Date.now());
    } catch {
      // keep current state
    }
  }, [enabled, lineId]);

  useEffect(() => {
    if (!enabled) {
      setStoppage(null);
      return;
    }
    fetchAlert();
    const timer = setInterval(fetchAlert, POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, fetchAlert]);

  // A different stoppage starts with an empty form.
  const stoppageId = stoppage?.id ?? null;
  useEffect(() => {
    setReason(null);
    setNote('');
    setError(null);
  }, [stoppageId]);

  if (!enabled || !stoppage) return null;

  const minutes = Math.max(stoppage.stopped_minutes, Math.floor((now - new Date(stoppage.started_at).getTime()) / 60_000));
  const noteRequired = reason === 'other';
  const canSave = !!reason && (!noteRequired || note.trim() !== '') && !saving;

  const save = async () => {
    if (!reason) {
      setError(t('stoppageAlert.pickReason'));
      return;
    }
    if (noteRequired && note.trim() === '') {
      setError(t('stoppageAlert.noteRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/line-stoppages/${stoppage.id}/reason`, { reason, note: note.trim() || null });
      setStoppage(null);
    } catch (err: any) {
      // Someone else already gave the reason (another terminal) — nothing left to ask.
      if (err?.response?.status === 409) {
        setStoppage(null);
        return;
      }
      setError(err?.response?.data?.message ?? t('stoppageAlert.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.85)', zIndex: 9980,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, overflowY: 'auto',
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 28, maxWidth: 560, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.4)', fontFamily: 'Inter, system-ui, sans-serif', textAlign: 'center',
      }}>
        <div className="downtime-pulse" style={{ fontSize: 40, marginBottom: 6 }}>⛔</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#dc3545', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>
          {t('stoppageAlert.badge')} · {selectedLine?.name}
        </div>
        <h2 style={{ margin: '0 0 10px 0', fontSize: 22, color: '#1e293b' }}>
          {t('stoppageAlert.title', { duration: formatDuration(minutes) })}
        </h2>

        <div style={{ background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 10, padding: '10px 16px', marginBottom: 14 }}>
          <div style={{ fontSize: 15, color: '#721c24' }}>
            {t('stoppageAlert.since')}: <strong>{stoppage.started_at_local}</strong>
          </div>
        </div>

        <p style={{ margin: '0 0 12px 0', fontSize: 17, color: '#1e293b', fontWeight: 600 }}>
          {t('stoppageAlert.question')}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
          {STOPPAGE_REASONS.map(r => {
            const active = reason === r;
            return (
              <button
                key={r}
                type="button"
                onClick={() => { setReason(r); setError(null); }}
                style={{
                  padding: '14px 8px', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: 'pointer',
                  background: active ? '#667eea' : '#f1f5f9',
                  color: active ? '#fff' : '#334155',
                  border: `2px solid ${active ? '#667eea' : '#cbd5e1'}`,
                }}
              >
                {t(`stoppageAlert.reasons.${r}`)}
              </button>
            );
          })}
        </div>

        <textarea
          value={note}
          onChange={e => { setNote(e.target.value); setError(null); }}
          maxLength={500}
          rows={3}
          placeholder={noteRequired ? t('stoppageAlert.notePlaceholderRequired') : t('stoppageAlert.notePlaceholder')}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8, fontSize: 15,
            border: `1px solid ${noteRequired && note.trim() === '' ? '#f59e0b' : '#cbd5e1'}`, resize: 'vertical', marginBottom: 12,
            fontFamily: 'inherit',
          }}
        />

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>{error}</div>
        )}

        <button
          onClick={save}
          disabled={!canSave}
          style={{
            width: '100%', padding: '16px 0', background: '#667eea', color: '#fff', border: 'none', borderRadius: 10,
            fontSize: 18, fontWeight: 800, cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.5,
          }}
        >
          {saving ? t('stoppageAlert.saving') : `✔ ${t('stoppageAlert.save')}`}
        </button>
        <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#94a3b8' }}>{t('stoppageAlert.hint')}</p>
      </div>
    </div>
  );
};

export default LineStoppageAlertModal;
